// =============================================================================
// NotificationPreferencesPage — per-type email / in-app channel toggles
// =============================================================================
// Lets the user control which lifecycle events reach them over email and the
// in-app inbox. Transactional defaults are opt-OUT (enabled unless toggled
// off). Each toggle PATCHes a single preference row; the list is optimistic.
// =============================================================================

import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Spinner } from '@tkpod/ui';

import { api } from '@/lib/api';
import {
  notificationKeys,
  PREFERENCE_LABELS,
  type NotificationPreference,
} from '@/lib/notifications';

export function NotificationPreferencesPage() {
  const queryClient = useQueryClient();

  const prefsQuery = useQuery<NotificationPreference[], Error>({
    queryKey: notificationKeys.preferences,
    queryFn: () =>
      api.get<NotificationPreference[]>('/api/v1/notifications/preferences'),
  });

  const update = useMutation<
    NotificationPreference,
    Error,
    { type: string; emailEnabled: boolean; portalEnabled: boolean }
  >({
    mutationFn: ({ type, emailEnabled, portalEnabled }) =>
      api.patch<NotificationPreference>(
        `/api/v1/notifications/preferences/${type}`,
        { emailEnabled, portalEnabled },
      ),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.preferences });
      const prev = queryClient.getQueryData<NotificationPreference[]>(
        notificationKeys.preferences,
      );
      queryClient.setQueryData<NotificationPreference[]>(
        notificationKeys.preferences,
        (cur) =>
          (cur ?? []).map((p) =>
            p.type === next.type
              ? { ...p, emailEnabled: next.emailEnabled, portalEnabled: next.portalEnabled }
              : p,
          ),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      const prev = (ctx as { prev?: NotificationPreference[] } | undefined)?.prev;
      if (prev) queryClient.setQueryData(notificationKeys.preferences, prev);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences }),
  });

  const prefs = prefsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Notification preferences"
          description="Choose how you're notified about account and order activity."
          action={
            <Link
              to="/notifications"
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Back to inbox
            </Link>
          }
        />

        {prefsQuery.isLoading && !prefsQuery.data ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" label="Loading preferences..." />
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border bg-surface-secondary px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-text-secondary">
              <span>Event</span>
              <span className="w-16 text-center">In-app</span>
              <span className="w-16 text-center">Email</span>
            </div>
            {prefs.map((p) => (
              <div
                key={p.type}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-4 py-3 last:border-b-0"
              >
                <span className="text-sm text-text-primary">
                  {PREFERENCE_LABELS[p.type] ?? p.type}
                </span>
                <div className="flex w-16 justify-center">
                  <Switch
                    checked={p.portalEnabled}
                    onChange={(v) =>
                      update.mutate({
                        type: p.type,
                        emailEnabled: p.emailEnabled,
                        portalEnabled: v,
                      })
                    }
                  />
                </div>
                <div className="flex w-16 justify-center">
                  <Switch
                    checked={p.emailEnabled}
                    onChange={(v) =>
                      update.mutate({
                        type: p.type,
                        emailEnabled: v,
                        portalEnabled: p.portalEnabled,
                      })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-4 text-xs text-text-tertiary">
          Critical security emails (password resets, email verification) are
          always sent and cannot be disabled here.
        </p>
      </Card>
    </div>
  );
}

function Switch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
        checked ? 'bg-brand-600' : 'bg-border'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
